import React from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, Users, Building2, BadgeCheck, ChevronDown, Filter, Check, Star, Lock } from 'lucide-react';
import { Counter } from './ui/Counter';
import { Reveal } from './ui/Reveal';
import { TTIco, IGIco, YTIco } from './page/Mockups';
type Creator = {
  name: string;
  photo: string;
  cat: string;
  country: string;
  tt: string;
  ig: string;
  yt: string;
  agency: string;
  excl: 'Exclusive' | 'Non-Exclusive';
  email: string;
};
const CREATORS: Creator[] = [{
  name: 'Michelle Brown',
  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=64&h=64',
  cat: 'Fashion',
  country: 'United States',
  tt: '2.2M',
  ig: '158.7K',
  yt: '-',
  agency: 'Clicks Talent',
  excl: 'Exclusive',
  email: 'john@clickstalent.com'
}, {
  name: 'Stephanie & Loreal',
  photo: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=64&h=64',
  cat: 'Beauty',
  country: 'United States',
  tt: '169.8K',
  ig: '456.8K',
  yt: '-',
  agency: 'Boost Society',
  excl: 'Exclusive',
  email: 'josh@boost-society.com'
}, {
  name: 'Marcus Chen',
  photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=64&h=64',
  cat: 'Tech',
  country: 'United Kingdom',
  tt: '82.9K',
  ig: '38.1K',
  yt: '286M',
  agency: 'Viral Nation',
  excl: 'Non-Exclusive',
  email: 'ena@viralnation.com'
}, {
  name: 'Julius Doyle',
  photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=64&h=64',
  cat: 'Lifestyle',
  country: 'United States',
  tt: '-',
  ig: '449.8K',
  yt: '2.9K',
  agency: 'Connect Management',
  excl: 'Exclusive',
  email: 'oliver@connectmgt.com'
}];
const FILTERS = ['Follower count', 'Average views', 'Engagement rate', 'Pricing', 'Gender', 'Location', 'Platform', 'Niche', 'Tags'];
export const RosterDatabase = () => {
  return <section className="py-24 bg-gray-50 overflow-hidden" data-id="element-2294" data-mp="3rl177m0nuohq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2295" data-mp="3rmvqyx074ak8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-center" data-id="element-2296" data-mp="adp70ma6cf2u">
          {/* Copy */}
          <Reveal data-id="element-2297" data-mp="5crp8at2tn1v">
            <div data-id="element-2298" data-mp="5c9pv5b4aspq">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zano-blush text-zano-pink text-xs font-semibold border border-zano-pink/20 mb-5" data-id="element-2299" data-mp="5fxepr5maqy4">
                <Users size={13} data-id="element-2300" data-mp="edze629pdbja" /> The Roster Database
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zano-charcoal mb-4 leading-tight" data-id="element-2301" data-mp="t241jjjpab8e">
                Every agency's roster, in one searchable database
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-id="element-2302" data-mp="12327469seqt3">
                The first of its kind — a live collection of 400 talent agency
                rosters in one searchable place. Their creators are based mainly
                in the US and UK, with strong representation across other
                European countries, Australia, Canada, the UAE (Dubai), and
                South Africa.
              </p>
              <div className="bg-gray-100/80 border border-gray-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-id="element-2303" data-mp="17p2niyzsgdfq">
                <div className="p-1.5 bg-white rounded-md shadow-sm shrink-0" data-id="element-2304" data-mp="1cndyjd1tktzj">
                  <Lock size={14} className="text-gray-500" data-id="element-2305" data-mp="xyzvp24el5iz" />
                </div>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-2306" data-mp="12r160ii0gm6d">
                  <span className="font-semibold text-gray-900" data-id="element-2307" data-mp="ozcvukrbo7s1">
                    Approved access required.
                  </span>{' '}
                  Anyone can post a casting call to our network of agencies, but
                  viewing the premium roster database and talent agency
                  directory requires an approved brand account.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 flex items-start gap-3" data-id="element-2308" data-mp="1mdod6f93hm9p">
                <div className="p-1.5 bg-white rounded-md shadow-sm shrink-0" data-id="element-2309" data-mp="1iccm05akosze">
                  <BadgeCheck size={14} className="text-emerald-600" data-id="element-2310" data-mp="1iegoqjwqjly3" />
                </div>
                <p className="text-xs text-emerald-900 leading-relaxed" data-id="element-2311" data-mp="1dj1ahqutfo15">
                  <span className="font-semibold" data-id="element-2312" data-mp="18lrm8ej5xiew">
                    Every agency is vetted by our team.
                  </span>{' '}
                  Before an agency can create an account, we meet with them
                  one-on-one to verify who they are and the talent they
                  represent — and we check in regularly to keep every roster
                  legitimate and up to date.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2313" data-mp="2p09foedad86d">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-id="element-2314" data-mp="2ozrkll2d1001">
                  <p className="text-3xl font-bold text-zano-charcoal" data-id="element-2315" data-mp="2k1zlo73asdmc">
                    <Counter to={400} suffix="+" data-id="element-2316" data-mp="2rjxppw2pmwi6" />
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-2317" data-mp="2wjshs2y81s4t">talent agencies</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-id="element-2318" data-mp="31b0p8tfzr3fc">
                  <p className="text-3xl font-bold text-zano-charcoal" data-id="element-2319" data-mp="369vh0kvxs2bl">
                    <Counter to={6000} suffix="+" data-id="element-2320" data-mp="3692b1m4sxgui" />
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-2321" data-mp="28nemfnel82tj">represented creators</p>
                </div>
              </div>

              <ul className="space-y-3" data-id="element-2322" data-mp="2dlg6obnr286l">
                <li className="flex items-start gap-3" data-id="element-2323" data-mp="2ikiyuvimsjx5">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-2324" data-mp="2iizk59cmigxh">
                    <Building2 size={15} className="text-zano-charcoal" data-id="element-2325" data-mp="2mnfoh66l9gal" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-2326" data-mp="3tywjlri38j8y">
                    <span className="font-semibold text-zano-charcoal" data-id="element-2327" data-mp="1v42uvqtjj0q">
                      Filter by agency
                    </span>{' '}
                    — already partnering with agencies? Pull up their live
                    rosters instantly to consider talent for a campaign.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2328" data-mp="27voe3eyz2bkd">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-2329" data-mp="22y4mzeh6e4b0">
                    <BadgeCheck size={15} className="text-zano-charcoal" data-id="element-2330" data-mp="1f8aaiy0rqhra" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-2331" data-mp="1b6p2pr33ctny">
                    <span className="font-semibold text-zano-charcoal" data-id="element-2332" data-mp="1b89whehl08kr">
                      Exclusively represented only
                    </span>{' '}
                    — work only with exclusively represented creators by
                    filtering for exclusive only.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2333" data-mp="wfpvokhwmd6h">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-2334" data-mp="rhofq8frytz8">
                    <SlidersHorizontal size={15} className="text-zano-charcoal" data-id="element-2335" data-mp="335t7vk9lu36n" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-2336" data-mp="2u5d8alvzfg72">
                    Filter by follower count, views, engagement, agency-set
                    pricing, gender, location, platform, niche, tags, and
                    free-text keywords.
                  </span>
                </li>
              </ul>
            </div>
          </Reveal>

          {/* Layered visual */}
          <Reveal delay={0.1} data-id="element-2337" data-mp="3qtjh7kzxsur5">
            <div className="relative" data-id="element-2338" data-mp="3cw4mc0qwzurs">
              {/* Glow */}
              <div className="absolute -inset-6 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2339" data-mp="3cxyxwogaqaj7" />

              {/* App window */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-float overflow-hidden flex flex-col h-[440px] text-left w-full" data-id="element-2340" data-mp="21feeyear8wwl">
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-2341" data-mp="2y3wpvrvye3kg">
                  <div data-id="element-2342" data-mp="2t40wemkvtuj5">
                    <h3 className="font-bold text-gray-900 text-base" data-id="element-2343" data-mp="2t2pi3stt94ds">
                      Roster Database
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-2344" data-mp="2o70qcbo18rs1">
                      6,000+ represented creators
                    </p>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden p-4 bg-gray-50" data-id="element-2345" data-mp="3gt3chpzvbfbj">
                  {/* Toolbar */}
                  <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" data-id="element-2346" data-mp="3bwv3kuc9kict">
                    <div className="relative flex-1 min-w-[110px]" data-id="element-2347" data-mp="372h004ng74x2">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2348" data-mp="325ql3kndqfd8" />
                      <input type="text" placeholder="Search" className="w-full pl-6 pr-2 py-1.5 border border-gray-200 rounded-md text-xs outline-none bg-white" data-id="element-2349" data-mp="3utw6m9m9c8e4" />
                    </div>
                    {['Platforms', 'Niche', 'Location'].map(f => <button key={f} className="flex items-center gap-1 border border-gray-200 bg-white px-2.5 py-1.5 rounded-md text-xs text-gray-700 whitespace-nowrap" data-id="element-2350" data-mp="fw9frhwwtxed">
                        {f} <ChevronDown size={10} data-id="element-2351" data-mp="twtwm8j9lj58" />
                      </button>)}
                    <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap" data-id="element-2352" data-mp="ynk4bsf5xy33">
                      <Filter size={12} data-id="element-2353" data-mp="18mhzhe73gpv2" /> All filters
                    </button>
                  </div>

                  {/* Active filter pills */}
                  <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-2354" data-mp="1dlujzsahtkuh">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-zano-blush text-zano-pink border border-zano-pink/30" data-id="element-2355" data-mp="uqutz1j7w0cj">
                      <Building2 size={11} data-id="element-2356" data-mp="13qqbrp7a2tlj" /> Agency: Clicks Talent
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-2357" data-mp="18hgesvs8ekd2">
                      <BadgeCheck size={11} data-id="element-2358" data-mp="1dg9qqjrve3b4" /> Exclusive only
                    </span>
                  </div>

                  {/* Roster table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-id="element-2359" data-mp="1mgzmxol1lvpd">
                    <table className="w-full text-left text-xs whitespace-nowrap" data-id="element-2360" data-mp="1rgucnot30f8u">
                      <thead className="bg-white border-b border-gray-100 text-gray-500" data-id="element-2361" data-mp="3o8ui8tmzrnlf">
                        <tr data-id="element-2362" data-mp="3t7pdiv25b4mf">
                          <th className="px-3 py-2.5 font-medium" data-id="element-2363" data-mp="3t6w39k3z9wzj">Name</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2364" data-mp="3lnwjhdclu3fn">
                            Category ↓
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2365" data-mp="3gwerhesk7d6t">Country</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2366" data-mp="3bxtppaxnslg9">
                            <TTIco data-id="element-2367" data-mp="3bw0i9tf81x77" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2368" data-mp="370v27b8i3cr1">
                            <IGIco data-id="element-2369" data-mp="8hwi99qvtrlm" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2370" data-mp="3iju8lo9yehg">
                            <YTIco data-id="element-2371" data-mp="3jcsszd2xxoi" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2372" data-mp="3uo32fer2nvm7">Agency</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-2373" data-mp="3qnj62debow14">
                            Exclusivity
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50" data-id="element-2374" data-mp="2f8cekgfwq776">
                        {CREATORS.map((c, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-2375" data-mp="2gozxqzwwvrig" data-mpi={i}>
                            <td className="px-3 py-2 flex items-center gap-2" data-id="element-2376" data-mp="2locjfhc1qsjg">
                              <img src={c.photo} alt={c.name} className="w-6 h-6 rounded-full object-cover shrink-0" data-id="element-2377" data-mp="2lm9vd2ugf9hd" />
                              <span className="font-medium text-gray-900 truncate max-w-[100px]" data-id="element-2378" data-mp="1xyi8x6f79g2d">
                                {c.name}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-2379" data-mp="1o1dmr6lctklk">{c.cat}</td>
                            <td className="px-3 py-2 text-gray-600 truncate max-w-[80px]" data-id="element-2380" data-mp="1sz5n8p02ef1d">
                              {c.country}
                            </td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-2381" data-mp="1f20j4ujwvq8r">{c.tt}</td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-2382" data-mp="1j0zogg9k1p0q">{c.ig}</td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-2383" data-mp="10e8k5we9u2au">{c.yt}</td>
                            <td className="px-3 py-2 text-gray-700 truncate max-w-[90px]" data-id="element-2384" data-mp="venk1ind7nwj">
                              {c.agency}
                            </td>
                            <td className="px-3 py-2" data-id="element-2385" data-mp="19bixroeka3ix">
                              {c.excl === 'Exclusive' ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-id="element-2386" data-mp="1jbtbda4idcym">
                                  <BadgeCheck size={11} data-id="element-2387" data-mp="1edi14yaax0fv" /> {c.excl}
                                </span> : <span className="text-gray-500" data-id="element-2388" data-mp="1s3va93yxil6s">{c.excl}</span>}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Floating "All filters" popup layer */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 20
            }} whileInView={{
              opacity: 1,
              y: 0,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.35
            }} className="absolute -bottom-10 -right-4 sm:-right-10 w-60 bg-white rounded-xl border border-gray-200 shadow-float p-4 z-20" data-id="element-2389" data-mp="2h4j76osisnln">
                <div className="flex items-center gap-2 mb-3" data-id="element-2390" data-mp="275tqgzcd196z">
                  <SlidersHorizontal size={14} className="text-zano-pink" data-id="element-2391" data-mp="2c6rehsk1wxge" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-2392" data-mp="3imtpsex4koai">
                    All filters
                  </p>
                </div>

                <div className="space-y-2 mb-3" data-id="element-2393" data-mp="3nkd6dlhapd5m">
                  <label className="flex items-center gap-2 rounded-lg bg-zano-blush/60 border border-zano-pink/20 px-2.5 py-1.5 cursor-pointer" data-id="element-2394" data-mp="1byb5ex0cu34i">
                    <span className="w-4 h-4 rounded bg-zano-pink flex items-center justify-center" data-id="element-2395" data-mp="170j4jdhqf6o6">
                      <Check size={11} className="text-white" data-id="element-2396" data-mp="126x2sgd3wn75" />
                    </span>
                    <span className="text-xs font-medium text-zano-charcoal flex items-center gap-1" data-id="element-2397" data-mp="x8n6g2fnbgjy">
                      <Building2 size={12} data-id="element-2398" data-mp="t8l21lij090z" /> By agency
                    </span>
                  </label>
                  <label className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 cursor-pointer" data-id="element-2399" data-mp="o75i5ws348k6">
                    <span className="w-4 h-4 rounded bg-emerald-500 flex items-center justify-center" data-id="element-2400" data-mp="eakdxdnw31he">
                      <Check size={11} className="text-white" data-id="element-2401" data-mp="2dneopmj2v7fj" />
                    </span>
                    <span className="text-xs font-medium text-emerald-800 flex items-center gap-1" data-id="element-2402" data-mp="28ok72nwujox0">
                      <BadgeCheck size={12} data-id="element-2403" data-mp="6zedu2xzcwze" /> Exclusive only
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap gap-1.5" data-id="element-2404" data-mp="bqebxbiu5l3h">
                  {FILTERS.map(f => <span key={f} className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-[10px] text-gray-500" data-id="element-2405" data-mp="3t7noet38iw58">
                      {f}
                    </span>)}
                </div>
              </motion.div>

              {/* Floating callout chip */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.6
            }} className="absolute -top-4 -left-3 hidden lg:flex items-center gap-2 bg-white border border-gray-100 shadow-float rounded-full pl-2 pr-3 py-1.5 z-20" data-id="element-2406" data-mp="umme26gjxfuk">
                <span className="w-5 h-5 rounded-full bg-zano-blush flex items-center justify-center" data-id="element-2407" data-mp="37vf0p16on9a7">
                  <Star size={11} className="text-zano-pink" data-id="element-2408" data-mp="32wvf51w03zid" />
                </span>
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap" data-id="element-2409" data-mp="2f711wj7rs8xl">
                  First of its kind — straight from the source
                </span>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
};